import { useQuery } from '@tanstack/react-query';
import type { ReactNode } from 'react';
import { api } from '../lib/api';
import { useI18n } from '../lib/i18n';
import type { ScoringDimension } from '../lib/types';
import { Badge, Card, CardHeader, Detail, PageHeader, Table, Td, Th, Tr } from '../components/ui';
import { useNumber } from '../components/kit';
import { Loaded, Note, useCode, useCompany, useDates, useFilterOptions } from '../components/b-shared';

/**
 * Dashboard Settings (CR-008 §6.2, §11, §12.3, §17), read-only: the version in force today — the scoring
 * weights and thresholds, company health, the risk thresholds, the report schedule, retention and AI. They are
 * changed in the admin (Settings › Executive Dashboard), each save a new version from its day on.
 */

interface DimensionWeights {
  weights: Record<ScoringDimension, number>;
  enabled: Record<ScoringDimension, boolean>;
}

interface DashboardSettings {
  version: number;
  effectiveFrom: string | null;
  scoring: DimensionWeights & {
    minEvidenceEvents: number;
    minMeasurableWeightPercent: number;
    reviewOverdueHours: number;
    labels: { excellent: number; veryGood: number; good: number };
    departmentOverrides: Array<DimensionWeights & { departmentId: string }>;
  };
  health: {
    weights: { financial: number; work: number; delivery: number; service: number };
    minMeasurableWeightPercent: number;
    minScoredEmployees: number;
    minTickets: number;
    minDatedTasks: number;
  };
  risks: {
    blockedQTaskDays: number;
    stalledWorkflowDays: number;
    contractExpiryDays: number;
    overdueReceivableSharePercent: number;
    expenseSpikePercent: number;
    receivableConcentrationPercent: number;
  };
  schedule: { dailyAt: string; monthlyAt: string; weekly: boolean; settleDays: number };
  retention: { snapshotMonths: number; exportLogMonths: number };
  ai: {
    enabled: boolean;
    model: string | null;
    monthlyTokenBudget: number | null;
    reports: { daily: boolean; weekly: boolean; monthly: boolean; employees: boolean };
    priceEstimate: { inputPerMillion: number | null; outputPerMillion: number | null; currency: string; asOf: string | null };
    availability: string;
    budget: number;
    used: number;
  };
}

const DIMENSIONS: ScoringDimension[] = ['completion', 'deadline', 'quality', 'tickets', 'collaboration'];
const PILLARS = ['financial', 'work', 'delivery', 'service'] as const;
const AI_REPORTS = ['daily', 'weekly', 'monthly', 'employees'] as const;

export function SettingsPage() {
  const { t } = useI18n();
  const settings = useQuery({ queryKey: ['executive', 'settings'], queryFn: () => api.get<DashboardSettings>('/executive/settings') });
  return (
    <>
      <PageHeader title={t('settings.title')} subtitle={t('settings.subtitle')} />
      <Loaded query={settings}>{(data) => <SettingsBody data={data} />}</Loaded>
    </>
  );
}

function OnOff({ on }: { on: boolean }) {
  const { t } = useI18n();
  return <Badge tone={on ? 'success' : 'neutral'}>{on ? t('settings.on') : t('settings.off')}</Badge>;
}

function Section({ title, subtitle, children, className }: { title: string; subtitle?: string; children: ReactNode; className?: string }) {
  return (
    <Card className={className}>
      <CardHeader title={title} subtitle={subtitle} />
      {children}
    </Card>
  );
}

function SettingsBody({ data }: { data: DashboardSettings }) {
  const { t, localized } = useI18n();
  const num = useNumber();
  const dates = useDates();
  const code = useCode();
  const filters = useFilterOptions();
  const { timezone } = useCompany();
  const s = data.scoring;
  const departmentName = (id: string) => {
    const d = filters.data?.departments.find((x) => x.id === id);
    return d ? localized(d) : t('settings.unknownDepartment');
  };
  const counted = data.ai.availability === 'ENABLED' || data.ai.availability === 'BUDGET_EXCEEDED';
  const price = data.ai.priceEstimate;

  return (
    <div className="space-y-5">
      <Card>
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
          <Badge tone="brand">{data.version === 0 ? t('settings.defaults') : t('settings.version', { n: num(data.version) })}</Badge>
          {data.effectiveFrom && <span className="text-body-sm text-[var(--text-secondary)]">{t('settings.inForceSince', { date: dates.day(data.effectiveFrom) })}</span>}
        </div>
        <div className="mt-3 space-y-1.5">
          <Note>{t('settings.readOnly')}</Note>
          <Note>{t('settings.versions')}</Note>
        </div>
      </Card>

      <div className="grid gap-5 xl:grid-cols-2">
        <Section title={t('settings.scoring.title')} subtitle={t('settings.scoring.subtitle')}>
          <Table>
            <thead>
              <tr>
                <Th>{t('employee.dimensions.dimension')}</Th>
                <Th>{t('settings.scoring.state')}</Th>
                <Th align="end">{t('employee.dimensions.weight')}</Th>
              </tr>
            </thead>
            <tbody>
              {DIMENSIONS.map((d) => (
                <Tr key={d}>
                  <Td className="font-medium">{t(`dimension.${d}`)}</Td>
                  <Td>
                    <OnOff on={s.enabled[d]} />
                  </Td>
                  <Td align="end" className="tabular">
                    {s.enabled[d] ? `${num(s.weights[d])}%` : <span className="text-muted">—</span>}
                  </Td>
                </Tr>
              ))}
            </tbody>
          </Table>
          <dl className="mt-4">
            <Detail label={t('settings.scoring.minEvents')}>{num(s.minEvidenceEvents)}</Detail>
            <Detail label={t('settings.scoring.minWeight')}>{num(s.minMeasurableWeightPercent)}%</Detail>
            <Detail label={t('settings.scoring.reviewHours')}>{num(s.reviewOverdueHours)}</Detail>
          </dl>
        </Section>

        <Section title={t('settings.labels.title')} subtitle={t('settings.labels.subtitle')}>
          <dl>
            <Detail label={t('employees.label.EXCELLENT')}>{t('settings.labels.atLeast', { n: num(s.labels.excellent) })}</Detail>
            <Detail label={t('employees.label.VERY_GOOD')}>{t('settings.labels.atLeast', { n: num(s.labels.veryGood) })}</Detail>
            <Detail label={t('employees.label.GOOD')}>{t('settings.labels.atLeast', { n: num(s.labels.good) })}</Detail>
            <Detail label={t('employees.label.NEEDS_ATTENTION')}>{t('settings.labels.below', { n: num(s.labels.good) })}</Detail>
          </dl>
          <Note className="mt-3">{t('settings.labels.note')}</Note>
        </Section>
      </div>

      <Section title={t('settings.overrides.title')} subtitle={t('settings.overrides.subtitle')}>
        {s.departmentOverrides.length === 0 ? (
          <p className="text-body-sm text-muted">{t('settings.overrides.none')}</p>
        ) : (
          <Table>
            <thead>
              <tr>
                <Th>{t('common.department')}</Th>
                {DIMENSIONS.map((d) => (
                  <Th key={d} align="end">
                    {t(`dimension.${d}`)}
                  </Th>
                ))}
              </tr>
            </thead>
            <tbody>
              {s.departmentOverrides.map((o) => (
                <Tr key={o.departmentId}>
                  <Td className="font-medium">{departmentName(o.departmentId)}</Td>
                  {DIMENSIONS.map((d) => (
                    <Td key={d} align="end" className="tabular">
                      {o.enabled[d] ? `${num(o.weights[d])}%` : <span className="text-muted">{t('settings.off')}</span>}
                    </Td>
                  ))}
                </Tr>
              ))}
            </tbody>
          </Table>
        )}
      </Section>

      <div className="grid gap-5 xl:grid-cols-2">
        <Section title={t('settings.health.title')} subtitle={t('settings.health.subtitle')}>
          <dl>
            {PILLARS.map((p) => (
              <Detail key={p} label={t(`settings.health.${p}`)}>
                {num(data.health.weights[p])}%
              </Detail>
            ))}
          </dl>
          <dl className="mt-4 border-t hairline pt-3">
            <Detail label={t('settings.health.minWeight')}>{num(data.health.minMeasurableWeightPercent)}%</Detail>
            <Detail label={t('settings.health.minScored')}>{num(data.health.minScoredEmployees)}</Detail>
            <Detail label={t('settings.health.minTickets')}>{num(data.health.minTickets)}</Detail>
            <Detail label={t('settings.health.minDatedTasks')}>{num(data.health.minDatedTasks)}</Detail>
          </dl>
        </Section>

        <Section title={t('settings.risks.title')} subtitle={t('settings.risks.subtitle')}>
          <dl>
            <Detail label={t('settings.risks.blockedQTaskDays')}>{num(data.risks.blockedQTaskDays)}</Detail>
            <Detail label={t('settings.risks.stalledWorkflowDays')}>{num(data.risks.stalledWorkflowDays)}</Detail>
            <Detail label={t('settings.risks.contractExpiryDays')}>{num(data.risks.contractExpiryDays)}</Detail>
            <Detail label={t('settings.risks.overdueReceivableShare')}>{num(data.risks.overdueReceivableSharePercent)}%</Detail>
            <Detail label={t('settings.risks.expenseSpike')}>{num(data.risks.expenseSpikePercent)}%</Detail>
            <Detail label={t('settings.risks.receivableConcentration')}>{num(data.risks.receivableConcentrationPercent)}%</Detail>
          </dl>
        </Section>
      </div>

      <div className="grid gap-5 xl:grid-cols-2">
        <Section title={t('settings.schedule.title')} subtitle={timezone ? t('period.timezone', { tz: timezone }) : undefined}>
          <dl>
            <Detail label={t('settings.schedule.daily')}>
              <span dir="ltr" className="tabular">
                {data.schedule.dailyAt}
              </span>
            </Detail>
            <Detail label={t('settings.schedule.monthly')}>
              <span dir="ltr" className="tabular">
                {data.schedule.monthlyAt}
              </span>
            </Detail>
            <Detail label={t('settings.schedule.weekly')}>
              <OnOff on={data.schedule.weekly} />
            </Detail>
            <Detail label={t('settings.schedule.settleDays')}>{num(data.schedule.settleDays)}</Detail>
          </dl>
          <Note className="mt-3">{t('settings.schedule.note')}</Note>
        </Section>

        <Section title={t('settings.retention.title')}>
          <dl>
            <Detail label={t('settings.retention.snapshots')}>
              {data.retention.snapshotMonths === 0 ? t('settings.retention.forever') : num(data.retention.snapshotMonths)}
            </Detail>
            <Detail label={t('settings.retention.exports')}>
              {data.retention.exportLogMonths === 0 ? t('settings.retention.forever') : num(data.retention.exportLogMonths)}
            </Detail>
          </dl>
        </Section>
      </div>

      <Section title={t('settings.ai.title')} subtitle={t('settings.ai.subtitle')}>
        <div className="grid gap-x-8 md:grid-cols-2">
          <dl>
            <Detail label={t('settings.ai.switch')}>
              <OnOff on={data.ai.enabled} />
            </Detail>
            <Detail label={t('settings.ai.availability')}>
              <Badge tone={data.ai.availability === 'ENABLED' ? 'success' : data.ai.availability === 'BUDGET_EXCEEDED' ? 'warning' : 'neutral'}>
                {code('settings.ai.state', data.ai.availability)}
              </Badge>
            </Detail>
            <Detail label={t('settings.ai.model')}>
              {data.ai.model ? (
                <span dir="ltr" className="font-mono text-caption">
                  {data.ai.model}
                </span>
              ) : (
                <span className="text-muted">{t('settings.ai.noModel')}</span>
              )}
            </Detail>
            <Detail label={t('settings.ai.budget')}>{num(data.ai.budget)}</Detail>
            <Detail label={t('settings.ai.used')}>{counted ? num(data.ai.used) : <span className="text-muted">{t('settings.ai.notCounted')}</span>}</Detail>
          </dl>
          <dl>
            {AI_REPORTS.map((r) => (
              <Detail key={r} label={t(`settings.ai.reports.${r}`)}>
                <OnOff on={data.ai.reports[r]} />
              </Detail>
            ))}
            <Detail label={t('settings.ai.price')}>
              {price.inputPerMillion === null || price.outputPerMillion === null ? (
                <span className="text-muted">{t('settings.ai.noPrice')}</span>
              ) : (
                <span dir="ltr" className="tabular">
                  {t('settings.ai.priceValue', { input: num(price.inputPerMillion, 4), output: num(price.outputPerMillion, 4), currency: price.currency })}
                </span>
              )}
            </Detail>
            {price.asOf && <Detail label={t('settings.ai.priceAsOf')}>{dates.day(price.asOf)}</Detail>}
          </dl>
        </div>
        <div className="mt-3 space-y-1.5">
          <Note>{t('settings.ai.note')}</Note>
          <Note>{t('settings.ai.priceNote')}</Note>
        </div>
      </Section>
    </div>
  );
}
